// src/Screens/admin/FAQ/FAQManagement.jsx
import React, { useState, useEffect, useContext } from "react";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiHelpCircle,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiMessageCircle,
  FiCheckCircle,
  FiFileText,
  FiFilter,
  FiSave,
} from "react-icons/fi";
import { Form, Spinner } from "react-bootstrap";
import "../../../assets/css/admin/screens/faqmanagement/AdminFAQManagement.css";
import axios from "axios";
import AppContext from "../../../contexts/AppContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FAQManagement = () => {
  const { baseUrl, adminAuthToken } = useContext(AppContext);

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [status, setStatus] = useState("Active");
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(false);
  const [faqId, setFaqId] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const viewAllFaqs = () => {
    setLoading(true);
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    axios
      .post(`${baseUrl}/admin/faq/viewAllFaqs`, 
        { chadmin_id: adminId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminAuthToken}`,
          },
        },
      )
      .then((response) => {
        setFaqs(response.data.faq || []);
        setLoading(false);
        handleCloseModal();
      })
      .catch(() => {
        setFaqs([]);
        setLoading(false);
      });
  };

  const viewFaqStats = () => {
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    axios
      .get(`${baseUrl}/admin/faq/stats`, 
        { params: { chadmin_id: adminId } },
      {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminAuthToken}`,
          },
        })
      .then((response) => setStats(response.data))
      .catch((error) => console.log(error));
  };

  const filteredFaqs = faqs.filter((faq) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      faq.question?.toLowerCase().includes(q) ||
      faq.answer?.toLowerCase().includes(q);
    const matchesStatus =
      filterStatus === "All" ||
      (filterStatus === "Active" && faq.status === "Active") ||
      (filterStatus === "Draft" && faq.status === "Draft");
    return matchesSearch && matchesStatus;
  });

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  const createFaq = () => {
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    const formdata = new FormData();
    formdata.append("chadmin_id", adminId);
    formdata.append("question", question);
    formdata.append("answer", answer);
    formdata.append("status", status);
    setFormLoading(true);
    axios
      .post(`${baseUrl}/admin/faq/createFaq`, formdata, {
        headers: { "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${adminAuthToken}`,
         },
      })
      .then(() => {
        toast.success("FAQ Added");
        viewAllFaqs();
        viewFaqStats();
        setFormLoading(false);
      })
      .catch(() => setFormLoading(false));
  };

  const updateFaq = () => {
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    const formdata = new FormData();
    formdata.append("chadmin_id", adminId);
    formdata.append("faqId", faqId);
    formdata.append("question", question);
    formdata.append("answer", answer);
    formdata.append("status", status);
    setFormLoading(true);
    axios
      .post(`${baseUrl}/admin/faq/updateFaq`, formdata, {
        headers: { "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${adminAuthToken}`,
         },
      })
      .then(() => {
        toast.info("FAQ Updated");
        viewAllFaqs();
        viewFaqStats();
        setFormLoading(false);
      })
      .catch(() => setFormLoading(false));
  };

  const deleteFaq = (id) => {
    const formdata = new FormData();
    formdata.append("faqId", id);
    axios
      .post(`${baseUrl}/admin/faq/deleteFaq`, formdata, {
        headers: { "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${adminAuthToken}`,
         },
      })
      .then(() => {
        toast.error("FAQ Deleted");
        viewAllFaqs();
        viewFaqStats();
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    viewAllFaqs();
    viewFaqStats();
  }, []);

  const handleOpenAddModal = () => {
    setEditingFaq(false);
    setFaqId("");
    setQuestion("");
    setAnswer("");
    setStatus("Active");
    setShowModal(true);
  };

  const handleOpenEditModal = (faq) => {
    setEditingFaq(true);
    setFaqId(faq.id);
    setQuestion(faq.question);
    setAnswer(faq.answer);
    setStatus(faq.status || "Active");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setQuestion("");
    setAnswer("");
    setFaqId("");
    setStatus("Active");
  };

  const handleSubmit = () => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Please fill in both question and answer.");
      return;
    }
    editingFaq ? updateFaq() : createFaq();
  };

  return (
    <AdminMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="faq-card-shell">
        {/* ── Header ── */}
        <div className="faq-header-section">
          <div>
            <h3 className="faq-section-title">FAQ Management</h3>
            <p className="faq-section-subtitle">
              Manage frequently asked questions for your website
            </p>
          </div>
          <button className="faq-create-btn" onClick={handleOpenAddModal}>
            <span>+</span> Add New FAQ
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div className="faq-summary-grid">
          <div className="faq-summary-card">
            <div>
              <span className="faq-label">Total FAQs</span>
              <h3>{stats?.total_faqs ?? "0"}</h3>
              <p className="faq-muted mb-0">All questions</p>
            </div>
            <div className="faq-card-icon">
              <FiMessageCircle size={22} />
            </div>
          </div>
          <div className="faq-summary-card">
            <div>
              <span className="faq-label">Published</span>
              <h3>{stats?.published_faqs ?? "0"}</h3>
              <p className="faq-muted mb-0">Visible to users</p>
            </div>
            <div className="faq-card-icon faq-card-icon--green">
              <FiCheckCircle size={22} />
            </div>
          </div>
          <div className="faq-summary-card">
            <div>
              <span className="faq-label">Draft</span>
              <h3>{stats?.draft_faqs ?? "0"}</h3>
              <p className="faq-muted mb-0">Not yet published</p>
            </div>
            <div className="faq-card-icon faq-card-icon--red">
              <FiFileText size={22} />
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="faq-toolbar">
          <div className="faq-search-group">
            <span className="faq-search-icon">
              <FiSearch size={14} />
            </span>
            <input
              className="faq-search-input"
              placeholder="Search by question or answer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="faq-filter-group">
            <FiFilter size={14} />
            <select
              className="faq-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="faq-layout">
          <div className="faq-table-wrapper">
            <table className="faq-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Question</th>
                  <th>Answer</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      <Spinner
                        animation="border"
                        size="sm"
                        className="faq-spinner"
                      />
                      <span className="ms-2 faq-muted faq-loading-text">
                        Loading...
                      </span>
                    </td>
                  </tr>
                )}

                {!loading && filteredFaqs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-5">
                      <div className="faq-empty-icon">💬</div>
                      <p className="faq-muted mt-2">
                        {faqs.length === 0
                          ? "No FAQs found."
                          : "No FAQs match the selected filters."}
                      </p>
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredFaqs.map((faq, index) => (
                    <tr key={faq.id}>
                      <td>
                        <span className="faq-id">#{index + 1}</span>
                      </td>
                      <td className="faq-question-cell">
                        <p className="faq-question-text">{faq.question}</p>
                      </td>
                      <td className="faq-answer-cell">
                        <div className="faq-answer-wrap">
                          <p
                            className={`faq-answer-text ${expandedId === faq.id ? "expanded" : ""}`}
                          >
                            {faq.answer}
                          </p>
                          {faq.answer?.length > 100 && (
                            <button
                              className="faq-toggle-btn"
                              onClick={() => toggleExpand(faq.id)}
                            >
                              {expandedId === faq.id ? (
                                <>
                                  <FiChevronUp size={12} /> Show less
                                </>
                              ) : (
                                <>
                                  <FiChevronDown size={12} /> Show more
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`faq-status ${
                            faq.status === "Active"
                              ? "faq-status--active"
                              : "faq-status--draft"
                          }`}
                        >
                          {faq.status || "Active"}
                        </span>
                      </td>
                      <td>
                        <div className="faq-actions">
                          <button
                            className="faq-action-btn faq-action-btn--edit"
                            title="Edit"
                            onClick={() => handleOpenEditModal(faq)}
                          >
                            <FiEdit2 size={13} />
                          </button>
                          <button
                            className="faq-action-btn faq-action-btn--delete"
                            title="Delete"
                            onClick={() => deleteFaq(faq.id)}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div
          className="faq-modal-overlay"
          onClick={() => {
            setShowModal(false);
            handleCloseModal();
          }}
        >
          <div
            className="faq-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="faq-modal-header">
              <h4>{editingFaq ? "Edit FAQ" : "Add New FAQ"}</h4>
              <button
                className="faq-modal-close"
                onClick={() => {
                  handleCloseModal();
                  setShowModal(false);
                }}
              >
                <FiX size={20} />
              </button>
            </div>

            <Form className="faq-modal-form">
              <Form.Group className="faq-form-group">
                <Form.Label>
                  Question <span className="faq-required">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter the question..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="faq-form-group">
                <Form.Label>
                  Answer <span className="faq-required">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter the answer..."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="faq-form-group">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                </Form.Select>
              </Form.Group>

              {/* Modal Footer */}
              <div className="faq-modal-footer">
                <button
                  type="button"
                  className="faq-btn-cancel"
                  disabled={formLoading}
                  onClick={() => {
                    setShowModal(false);
                    handleCloseModal();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="faq-btn-save"
                  disabled={formLoading}
                  onClick={handleSubmit}
                >
                  {formLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {editingFaq ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    <>
                      <FiSave size={14} className="me-1" />
                      {editingFaq ? "Update FAQ" : "Add FAQ"}
                    </>
                  )}
                </button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </AdminMasterLayout>
  );
};

export default FAQManagement;